import {isoDateUtcSchema} from 'common/src/modules/shared/copilot/copilot.schemas';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {TimezoneObject} from 'common/src/modules/timezones/timezones.types';
import forceTimeZone from 'common/src/modules/timezones/utils/forceTimeZone';
import {CLStore} from 'common/src/redux/store';
import _find from 'lodash/find';
import _map from 'lodash/map';
import {DateTime} from 'luxon';
import {z} from 'zod';
import analyticsApi from '../../../../analytics.api';
import {ANALYTICS_LIMIT} from '../../../../analytics.constants';
import {
  AnalyticsFilterItem,
  DimensionValue,
  MetricsGroupBy,
  MetricValue,
} from '../../../../analytics.types';
import getDimensionMainParameter from '../../../../utils/getDimensionMainParameter';
import getRemoteDimensionKey from '../../../../utils/getRemoteDimensionKey';
import isUniqueDimensionValue from '../../../../utils/isUniqueDimensionValue';
import {CampaignAnalyticsToolName} from '../campaignAnalyticsAgent.enums';

const inputSchema = z.object({
  dimension: z
    .string()
    .default(DimensionValue.Date)
    .describe(
      'The viewing dimension to group the data by. When omitted, data is grouped by date only.',
    ),
  metrics: z
    .array(z.string())
    .describe('The Requested metrics. Must be taken from the "Available metrics" list'),
  startDate: isoDateUtcSchema.describe('Requested start date (start of the day)'),
  endDate: isoDateUtcSchema.describe('Requested end date (end of the day)'),
  filter: z
    .object({
      dimension: z.string().describe('The filtering dimension'),
      values: z.array(z.string()),
    })
    .nullable()
    .optional(),
  limit: z.number().describe('Number of items returned').default(5).nullable().optional(),
});

function getCampaignAnalyticsDataTool(store: CLStore) {
  function handleNoAnalyticsData() {
    return toolResult('Sorry campaign data is unavailable, please try again later');
  }

  async function execute({
    input: {dimension: aiDimension, metrics: aiMetrics, startDate, endDate, filter, limit = 5},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const {
      campaignPage: {
        insightsPage: {measuredPeriod, availableFilters, availableDimensions},
        campaignTree: {campaign},
      },
    } = store.getState();

    const timezoneObject = _find(measuredPeriod.timezoneOptions, {
      abbr: measuredPeriod.timezone,
    }) as TimezoneObject;
    const dateRange = {
      startDate: forceTimeZone(DateTime.fromISO(startDate).toUTC(), 0),
      endDate: forceTimeZone(DateTime.fromISO(endDate).toUTC(), 0).endOf('day'),
    };

    const metrics = aiMetrics as MetricValue[];

    const dimension =
      getRemoteDimensionKey<DimensionValue>(availableDimensions, aiDimension) ??
      DimensionValue.Date;

    const dimensions = [dimension];

    const filterKey = getRemoteDimensionKey<DimensionValue>(availableFilters, filter?.dimension);

    const campaignIdFilter = {
      filterBy: DimensionValue.CampaignID,
      allValues: [String(campaign.id)],
    };

    const allFilters = [
      campaignIdFilter,
      ...(filterKey
        ? [
            {
              filterBy: filterKey,
              allValues: filter?.values ?? [],
            },
          ]
        : []),
    ];

    if ([DimensionValue.Date, DimensionValue.Month, DimensionValue.Week].includes(dimension)) {
      function getGroupBy() {
        switch (dimension) {
          case DimensionValue.Month:
            return MetricsGroupBy.Month;
          case DimensionValue.Week:
            return MetricsGroupBy.Week;
          default:
            return MetricsGroupBy.Day;
        }
      }

      const analyticsData = await analyticsApi.getAnalyticsData({
        dimensions,
        metrics,
        dateRange,
        selectedTimezone: timezoneObject,
        groupBy: getGroupBy(),
        filters: allFilters,
        eventType: null,
      });

      if (!analyticsData.length) {
        return handleNoAnalyticsData();
      }

      return toolResult(JSON.stringify({analyticsData, dimensions}));
    } else {
      const {analyticsData: topPerformingData, dimensionValues} =
        await analyticsApi.getDashboardWidgetData({
          dimensions,
          metrics,
          dateRange,
          selectedTimezone: timezoneObject,
          limit,
          filters: allFilters,
          topPerforming: true,
          eventType: null,
        });

      const isUnique = isUniqueDimensionValue(dimension);
      const mainParameter = getDimensionMainParameter(isUnique, dimension);

      const dimensionFilter = {
        filterBy: mainParameter,
        allValues: _map(topPerformingData, (row) => row[mainParameter]),
      } as AnalyticsFilterItem;

      const dimensionFilterValues = _map(dimensionFilter.allValues, (value) => ({
        [mainParameter]: value,
      }));

      const analyticsData = await analyticsApi.getAnalyticsData({
        dimensions,
        metrics,
        dateRange,
        selectedTimezone: timezoneObject,
        groupBy: MetricsGroupBy.Day,
        filters: [...allFilters, dimensionFilter],
        eventType: null,
        limit: ANALYTICS_LIMIT,
      });

      if (!analyticsData.length) {
        return handleNoAnalyticsData();
      }

      return toolResult(
        JSON.stringify({
          analyticsData,
          dimensionValues,
          dimensionFilterValues,
          breakdownByTime: true,
          dimensions,
        }),
      );
    }
  }

  return tool({
    name: CampaignAnalyticsToolName.GetCampaignAnalyticsData,
    displayName: 'Loading Analytics Data',
    description:
      'Retrieves campaign performance over time, optionally broken down by a specified dimension always broken by time (day)',
    execute,
    inputSchema,
  });
}

export default getCampaignAnalyticsDataTool;
